import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { VmStatusModel, VmCreationStatus } from '../../models/vm-status.model';
import { VmStatusPollingService } from '../../services/vm-status-polling.service';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
  selector: 'app-vm-status-display',
  templateUrl: './vm-status-display.component.html',
  styleUrls: ['./vm-status-display.component.css']
})
export class VmStatusDisplayComponent implements OnInit, OnDestroy, OnChanges {
  @Input() ticketId: string = '';
  @Input() ticketNumber: string = '';

  vmStatus: VmStatusModel | null = null;
  isLoading = true;
  error: string | null = null;
  isPolling = false;
  lastUpdated: string = '';
  showHistory = false;
  history: any[] = [];

  constructor(
    private vmStatusPollingService: VmStatusPollingService,
    private message: NzMessageService
  ) {}

  ngOnInit(): void {
    console.log(`🚀 [VM Status Display] Component initialized`);
    console.log(`🎫 [VM Status Display] Ticket ID: ${this.ticketId}`);
    console.log(`🎫 [VM Status Display] Ticket Number: ${this.ticketNumber}`);
    console.log(`🔄 [VM Status Display] Starting initial VM status load...`);

    // Use setTimeout to avoid blocking the UI thread
    setTimeout(() => {
      this.loadVmStatus();
      // DON'T start polling here - let loadVmStatus decide based on status
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log(`🔄 [VM Status Display] Component inputs changed`, changes);
    if (changes['ticketId'] || changes['ticketNumber']) {
      // Stop any existing polling first
      this.stopPolling();
      
      // Load status but don't automatically restart polling
      setTimeout(() => {
        this.loadVmStatus();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /**
   * Load VM status from polling service
   */
  public loadVmStatus(): void {
    console.log(`🔄 [VM Status Display] Loading VM status for ticket ${this.ticketId}`);

    if (!this.ticketId) {
      console.log(`❌ [VM Status Display] No ticket ID provided`);
      this.error = 'Ticket ID is required';
      this.isLoading = false;
      return;
    }

    console.log(`⏳ [VM Status Display] Setting loading state to true`);
    this.isLoading = true;
    this.error = null;

    this.vmStatusPollingService.getVmStatus(this.ticketId).subscribe({
      next: (status) => {
        console.log(`✅ [VM Status Display] Got VM status:`, status);
        console.log(`📊 [VM Status Display] VM Creation Status: ${status?.vmCreationStatus}`);
        console.log(`🖥️ [VM Status Display] VM Name: ${status?.vmName || 'N/A'}`);
        console.log(`🌐 [VM Status Display] VM IP: ${status?.publicIpAddress || 'N/A'}`);

        this.vmStatus = status;
        this.isLoading = false;
        this.lastUpdated = new Date().toISOString();
        this.error = null;
        console.log(`✅ [VM Status Display] VM status loaded successfully`);
        
        // Check if status is final - if so, DON'T start polling
        if (status.vmCreationStatus === VmCreationStatus.Succeeded || 
            status.vmCreationStatus === VmCreationStatus.Failed) {
          console.log(`🛑 [VM Status Display] Status is already final (${status.vmCreationStatus}), NOT starting polling`);
          this.stopPolling(); // Make sure no polling starts
        } else {
          // Only start polling if status is in progress
          console.log(`▶️ [VM Status Display] Status is in progress (${status.vmCreationStatus}), starting polling`);
          this.startStatusPolling();
        }
      },
      error: (error) => {
        console.log(`❌ [VM Status Display] Error loading VM status:`, error);
        console.log(`📊 [VM Status Display] Error status: ${error.status}`);
        console.log(`📝 [VM Status Display] Error message: ${error.message}`);

        this.error = 'Failed to load VM status';
        this.isLoading = false;
        this.message.error(this.error);
      }
    });
  }

  /**
   * Start real-time status polling
   */
  private startStatusPolling(): void {
    console.log(`🔄 [VM Status Display] startStatusPolling called for ticket ${this.ticketId}`);
    
    // Guard against starting if no ticket ID
    if (!this.ticketId) {
      console.log(`⚠️ [VM Status Display] No ticket ID, cannot start polling`);
      return;
    }
    
    // Guard against starting if status is already final
    if (!this.shouldPoll()) {
      console.log(`⚠️ [VM Status Display] Should not poll (status is final), skipping`);
      return;
    }
    
    console.log(`📊 [VM Status Display] Should poll? ${this.shouldPoll()}`);
    console.log(`✅ [VM Status Display] Starting polling...`);

    this.isPolling = true;

    try {
      this.vmStatusPollingService.startPolling(this.ticketId, (status) => {
        console.log(`🔄 [VM Status Display] Polling update received:`, status);
        this.vmStatus = status;
        this.lastUpdated = new Date().toISOString();
        
        // Stop polling if status is final
        if (status.vmCreationStatus === VmCreationStatus.Succeeded || 
            status.vmCreationStatus === VmCreationStatus.Failed) {
          console.log(`🛑 [VM Status Display] Final status reached (${status.vmCreationStatus}), stopping polling`);
          this.stopPolling();
        }
      });
      console.log(`✅ [VM Status Display] Polling started successfully`);
    } catch (error) {
      console.error(`❌ [VM Status Display] Error starting polling:`, error);
      this.isPolling = false;
    }
  }

  /**
   * Stop active polling
   */
  private stopPolling(): void {
    if (this.ticketId) {
      this.vmStatusPollingService.stopPolling(this.ticketId);
      this.isPolling = false;
    }
  }

  /**
   * Restart polling with delay
   */
  private restartPolling(): void {
    this.stopPolling();
    setTimeout(() => {
      this.startStatusPolling();
    }, 100);
  }

  /**
   * Determine if polling should be active based on current status
   */
  private shouldPoll(): boolean {
    if (!this.vmStatus) {
      console.log('⚠️ [VM Status Display] shouldPoll: No VM status yet, not polling');
      return false;
    }

    const shouldPoll = this.vmStatus.vmCreationStatus === VmCreationStatus.InProgress ||
           this.vmStatus.vmCreationStatus === VmCreationStatus.NotStarted;
    
    console.log(`📊 [VM Status Display] shouldPoll: ${shouldPoll} (status: ${this.vmStatus.vmCreationStatus})`);
    
    return shouldPoll;
  }

  /**
   * Get status badge configuration
   */
  getStatusBadge(): any {
    if (!this.vmStatus) return null;

    const statusConfig = {
      [VmCreationStatus.NotStarted]: {
        text: 'Not Started',
        color: 'default',
        icon: 'clock-circle',
        nzStatus: 'default'
      },
      [VmCreationStatus.InProgress]: {
        text: 'In Progress',
        color: 'processing',
        icon: 'loading',
        nzStatus: 'processing'
      },
      [VmCreationStatus.Succeeded]: {
        text: 'Completed',
        color: 'success',
        icon: 'check-circle',
        nzStatus: 'success'
      },
      [VmCreationStatus.Failed]: {
        text: 'Failed',
        color: 'error',
        icon: 'close-circle',
        nzStatus: 'error'
      }
    };

    return statusConfig[this.vmStatus.vmCreationStatus];
  }

  /**
   * Get user-friendly status message
   */
  getStatusMessage(): string {
    if (!this.vmStatus) return '';

    const messages = {
      [VmCreationStatus.NotStarted]: 'VM creation has not started yet',
      [VmCreationStatus.InProgress]: `Azure VM is being created... (${this.vmStatus.progressPercentage || 0}%)`,
      [VmCreationStatus.Succeeded]: `VM creation completed successfully. VM: ${this.vmStatus.vmName || 'Unknown'}`,
      [VmCreationStatus.Failed]: `VM creation failed: ${this.vmStatus.failureMessage || 'Unknown error'}`
    };

    return messages[this.vmStatus.vmCreationStatus];
  }

  /**
   * Check if retry functionality is available
   */
  canRetry(): boolean {
    return this.vmStatus?.vmCreationStatus === VmCreationStatus.Failed;
  }

  /**
   * Retry VM creation
   */
  retryVmCreation(): void {
    if (!this.canRetry() || !this.ticketId) return;

    this.vmStatusPollingService.retryVmCreation(this.ticketId).subscribe({
      next: (response) => {
        this.message.success('VM creation retry initiated');
        this.loadVmStatus(); // Reload status to show updated state
      },
      error: (error) => {
        this.message.error('Failed to retry VM creation');
        console.error('Retry error:', error);
      }
    });
  }

  /**
   * Toggle status history display
   */
  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    if (this.showHistory) {
      this.loadStatusHistory();
    }
  }

  /**
   * Load status history
   */
  private loadStatusHistory(): void {
    if (!this.ticketId) return;

    this.vmStatusPollingService.getStatusHistory(this.ticketId).subscribe({
      next: (history) => {
        this.history = history || [];
      },
      error: (error) => {
        console.error('Error loading status history:', error);
        this.history = [];
      }
    });
  }

  /**
   * Get timeline color for history entries
   */
  getTimelineColor(status: string): 'red' | 'green' | 'blue' | 'grey' | 'gray' {
    const colorMap = {
      [VmCreationStatus.NotStarted]: 'gray',
      [VmCreationStatus.InProgress]: 'blue',
      [VmCreationStatus.Succeeded]: 'green',
      [VmCreationStatus.Failed]: 'red'
    };

    return (colorMap[status as VmCreationStatus] || 'gray') as 'red' | 'green' | 'blue' | 'grey' | 'gray';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { timeZone: 'Asia/Hong_Kong' });
  }

  /**
   * Format relative time for display
   */
  getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} minutes ago`;
    } else if (diffMins < 1440) {
      const hours = Math.floor(diffMins / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffMins / 1440);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  }

  /**
   * Get estimated completion time display
   */
  getEstimatedTimeDisplay(): string {
    if (!this.vmStatus?.estimatedCompletionTime) return '';

    const estimatedTime = new Date(this.vmStatus.estimatedCompletionTime);
    const now = new Date();
    const diffMs = estimatedTime.getTime() - now.getTime();

    if (diffMs < 0) {
      return 'Overdue';
    } else if (diffMs < 60000) {
      return `In ${Math.floor(diffMs / 60000)} minute${Math.floor(diffMs / 60000) > 1 ? 's' : ''}`;
    } else if (diffMs < 3600000) {
      return `In ${Math.floor(diffMs / 3600000)} hour${Math.floor(diffMs / 3600000) > 1 ? 's' : ''}`;
    } else {
      return `In ${Math.floor(diffMs / 86400000)} day${Math.floor(diffMs / 86400000) > 1 ? 's' : ''}`;
    }
  }

  /**
   * Check if VM details should be shown
   */
  shouldShowVmDetails(): boolean {
    return !!this.vmStatus?.vmName;
  }

  /**
   * Get VM connection information for display
   */
  getVmConnectionInfo(): any[] {
    if (!this.vmStatus) return [];

    const info = [];

    if (this.vmStatus.publicIpAddress) {
      info.push({
        label: 'Public IP',
        value: this.vmStatus.publicIpAddress
      });
    }

    if (this.vmStatus.privateIpAddress) {
      info.push({
        label: 'Private IP',
        value: this.vmStatus.privateIpAddress
      });
    }

    if (this.vmStatus.adminUsername) {
      info.push({
        label: 'Admin Username',
        value: this.vmStatus.adminUsername
      });
    }

    return info;
  }
}